import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatCertificateNumber } from '@/utils/certificateUtils';
import { updateCertificateWithAudit } from '@/utils/auditUtils';
import { CertificateNumberInventory } from '@/utils/certificateInventoryTypes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  XCircle
} from 'lucide-react';
import { CertificateStatusBadge } from './CertificateStatusBadge';
import { toast } from '@/hooks/use-toast';

interface CertificateVerificationModalProps {
  certificate: CertificateNumberInventory | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerificationComplete?: () => void;
}

export function CertificateVerificationModal({
  certificate,
  open,
  onOpenChange,
  onVerificationComplete
}: CertificateVerificationModalProps) {
  const [notes, setNotes] = useState('');
  const queryClient = useQueryClient();

  const verificationMutation = useMutation({
    mutationFn: async () => {
      if (!certificate) return null;
      
      const result = await updateCertificateWithAudit({
        certificateId: certificate.id,
        certificateNumber: certificate.certificate_number,
        status: 'verified',
        details: notes,
        actionType: 'verified'
      });
      
      if (!result.success) throw new Error('Verification failed');
      return result;
    },
    onSuccess: () => {
      toast.success("Verification Successful", "Certificate has been verified");
      
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['certificateInventory'] });
      queryClient.invalidateQueries({ queryKey: ['certificateAudit'] });
      queryClient.invalidateQueries({ queryKey: ['unverifiedCertificates'] });
      
      // Reset form
      setNotes('');
      
      // Close modal
      onOpenChange(false);
      
      // Call completion callback if provided
      if (onVerificationComplete) {
        onVerificationComplete();
      }
    },
    onError: (error) => {
      toast.error("Verification Failed", 
        `An error occurred: ${(error as Error).message}`);
    }
  });

  const handleVerify = async () => {
    if (!certificate) return;
    try {
      verificationMutation.mutate();
    } catch (error) {
      console.error('Verification error:', error);
      toast.error("Verification Failed", error instanceof Error ? error.message : "Unknown error occurred");
    }
  };

  if (!certificate) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(value) => {
      // Only allow closing if not in the middle of a verification
      if (!verificationMutation.isPending) {
        onOpenChange(value);
        if (!value) {
          setNotes('');
        }
      }
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Verify Certificate</DialogTitle>
          <DialogDescription>
            Verify that this certificate has been properly reported to regulatory authorities.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium mb-1">Certificate Number</p>
              <p className="text-sm font-semibold">
                {formatCertificateNumber(certificate.certificate_number)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">State</p>
              <p className="text-sm">{certificate.state}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium mb-1">Course Type</p>
              <p className="text-sm">{certificate.course_type}</p>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Current Status</p>
              <CertificateStatusBadge type="verification" item={certificate} />
            </div>
          </div>

          <div>
            <p className="text-sm font-medium mb-1">Verification Notes</p>
            <Textarea
              placeholder="Enter notes about this verification (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {certificate.verification_status === 'verified' && (
            <div className="bg-yellow-50 p-3 rounded-md text-yellow-800 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <span>This certificate is already verified. Re-verifying will create a new audit record.</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={verificationMutation.isPending}>
            Cancel
          </Button>
          <Button 
            onClick={handleVerify} 
            disabled={verificationMutation.isPending}
            className="flex items-center gap-2"
          >
            {verificationMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : certificate.verification_status === 'verified' ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Re-verify
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Verify
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
